import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageCircle, Tag, ShoppingBag, Package, Check, CheckCheck } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { notifications, user, isAuthenticated, markNotificationRead, markAllNotificationsRead } = useApp();

  if (!isAuthenticated) {
    return (
      <Layout title="Notifications">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <Bell className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in to view notifications</h2>
          <p className="text-gray-500 text-center mb-4">Stay updated with your activity</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700">
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  const userNotifications = (notifications || []).filter(n => n.user_id === user?.id || n.userId === user?.id);
  const unreadCount = userNotifications.filter(n => !n.read).length;

  const getIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'offer':
        return <Tag className="w-5 h-5 text-purple-500" />;
      case 'order':
        return <Package className="w-5 h-5 text-minted-500" />;
      case 'sale':
        return <ShoppingBag className="w-5 h-5 text-yellow-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Layout title="Notifications">
      {/* Header */}
      {userNotifications.length > 0 && (
        <div className="bg-white px-4 py-3 border-b flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
          </span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllNotificationsRead}
              className="text-minted-600 hover:text-minted-700"
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
      )}

      {userNotifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <Bell className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No notifications</h2>
          <p className="text-gray-500 text-center">You're all caught up!</p>
        </div>
      ) : (
        <div className="divide-y">
          {userNotifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => {
                markNotificationRead(notification.id);
                if (notification.link) navigate(notification.link);
              }}
              className={`w-full flex items-start gap-3 p-4 text-left transition-colors ${
                notification.read ? 'bg-white' : 'bg-minted-50'
              } hover:bg-gray-50`}
            >
              <div className={`p-2 rounded-full ${
                notification.read ? 'bg-gray-100' : 'bg-white'
              }`}>
                {getIcon(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className={`text-sm ${
                    notification.read ? 'font-normal text-gray-900' : 'font-semibold text-gray-900'
                  }`}>
                    {notification.title}
                  </h3>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {formatTime(notification.created_at || notification.createdAt)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>
              </div>
              {!notification.read && (
                <div className="w-2 h-2 bg-minted-600 rounded-full flex-shrink-0 mt-2" />
              )}
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
};

export default NotificationsPage;